import { z } from 'zod';

import type { TileSpec } from '#core/lib/expr/ops/dashboard/value.ts';
import { chartElementColor } from '#core/lib/formatters/colors.ts';

/**
 * A labeled reference line/area plotted on a dashboard's cartesian tiles.
 * Insights cite x-axis markers by byte-exact label and the chart emphasizes
 * the cited ones; y-axis markers are static reference context, never cited.
 * `color` is the resolved Mantine palette name, resolved with the same rule
 * the chart renderer uses, so insight chips hue-match their markers. The
 * schema doubles as the validation boundary where markers re-enter from
 * untyped thread state ({@link parseChartMarkers}).
 */
export const ChartMarkerSchema = z.object({
  label: z.string(),
  kind: z.enum(['line', 'area']),
  axis: z.enum(['x', 'y']),
  color: z.string(),
});

export type ChartMarker = z.infer<typeof ChartMarkerSchema>;

/**
 * The labeled reference lines/areas across a dashboard's cartesian tiles.
 * Unlabeled markers are not citable and are skipped — but they still occupy
 * a palette slot, so colors resolve on the original per-tile, per-kind
 * index exactly as the chart does.
 */
export const collectChartMarkers = (tiles: readonly TileSpec[]): readonly ChartMarker[] =>
  tiles.flatMap((tile) =>
    tile.kind === 'cartesian-spec'
      ? [
          ...tile.referenceLines.flatMap((line, i): readonly ChartMarker[] =>
            line.label
              ? [{ label: line.label, kind: 'line', axis: line.axis, color: chartElementColor(line.color, i) }]
              : [],
          ),
          ...tile.referenceAreas.flatMap((area, i): readonly ChartMarker[] =>
            area.label
              ? [{ label: area.label, kind: 'area', axis: area.axis, color: chartElementColor(area.color, i) }]
              : [],
          ),
        ]
      : [],
  );

/**
 * True when a time-axis line/area chart shipped with no x-axis markers —
 * the authoring miss the marker-repair flow branch corrects.
 */
export const hasUnannotatedTimeChart = (tiles: readonly TileSpec[]): boolean =>
  tiles.some(
    (tile) =>
      tile.kind === 'cartesian-spec' &&
      tile.xKind === 'time' &&
      tile.series.some((series) => series.type === 'line' || series.type === 'area') &&
      !tile.referenceLines.some((line) => line.axis === 'x') &&
      !tile.referenceAreas.some((area) => area.axis === 'x'),
  );

type CitationScanPart = {
  readonly type?: string;
  readonly toolName?: string;
  readonly args?: unknown;
  readonly result?: unknown;
};

/**
 * The structural slice of an assistant-ui thread message the citation scans
 * read. Live `MessageState` messages carry `parts`, persisted
 * `ThreadMessage`s carry `content`; the scans touch only the overlapping
 * tool-call fields, mirroring `recipesFromParts`.
 */
export type CitationScanMessage = {
  readonly parts?: readonly CitationScanPart[];
  readonly content?: readonly CitationScanPart[];
};

/** The tool-call parts across a message slice, in thread order. */
const toolCallParts = (messages: readonly CitationScanMessage[]) =>
  messages
    .flatMap((message) => message.parts ?? message.content ?? [])
    .filter((part) => part.type === 'tool-call' && typeof part.toolName === 'string');

/**
 * Marker labels cited by the insights call that belongs to the dashboard
 * at the given message index (the dashboard render's own
 * `state.message.index`): the first showInsights tool call after it, cut
 * off at any later showDashboard call (whose insights belong to that later
 * dashboard). Tool calls are matched by plain wire name — exactly the
 * values the `ToolsContext` registry provides — so callers pass the
 * context refs directly. Args may be mid-stream partials, so marker
 * values are validated structurally.
 */
export const citedMarkerLabels = (
  messages: readonly CitationScanMessage[],
  dashboardMessageIndex: number,
  refs: { readonly dashboard: string; readonly insights: string },
): readonly string[] => {
  const calls = toolCallParts(messages.slice(dashboardMessageIndex + 1));
  const boundary = calls.findIndex((part) => part.toolName === refs.dashboard);
  const scoped = boundary === -1 ? calls : calls.slice(0, boundary);
  const insightsArgs = scoped.find((part) => part.toolName === refs.insights)?.args as
    | { readonly insights?: readonly { readonly marker?: unknown }[] }
    | undefined;
  return (insightsArgs?.insights ?? []).flatMap((insight) =>
    typeof insight?.marker === 'string' && insight.marker !== '' ? [insight.marker] : [],
  );
};

/**
 * Flat `[label, color, label, color, …]` pairs of the markers plotted by
 * the nearest showDashboard result at or before the given message index —
 * the dashboard the insights call at that position cites. Flat primitives
 * because `useAuiState` selectors only bail out via `Object.is` /
 * `useShallow`, and thread state churns object identity on every streamed
 * token — the `useBindingScope` selector documents the same constraint.
 */
export const plottedMarkerPairs = (
  messages: readonly CitationScanMessage[],
  messageIndex: number,
  dashboardRef: string,
): readonly string[] => {
  const result = toolCallParts(messages.slice(0, messageIndex + 1))
    .filter((part) => part.toolName === dashboardRef)
    .at(-1)?.result;
  const markers = parseChartMarkers((result as { readonly markers?: unknown } | undefined)?.markers);
  return markers.flatMap((marker) => [marker.label, marker.color]);
};

/**
 * Validate a raw `markers` value from thread state into chart markers.
 * Tool results cross the thread-state boundary untyped; this is the single
 * validation point for chip rendering.
 */
export const parseChartMarkers = (value: unknown): readonly ChartMarker[] => {
  const parsed = ChartMarkerSchema.array().safeParse(value);
  return parsed.success ? parsed.data : [];
};
